import { useContext, useEffect, useState, useRef } from 'react';
import { DoubleCheckIcon } from 'features/compositions/assets/icons/icons';
import CompositionTableToolbar from './CompositionTableToolbar';
import styles from './EditableCompositionTable.module.css';
import EditableCompositionRow from './EditableCompositionRow';
import ThemeContext from 'config/context/themeContext';
import AddNewCompositionRows from './add-new-rows/AddNewCompositionRows';
import AddNewRowsControl from './add-new-rows/AddNewRowsControl';
import { RequireAuth } from 'features/permissions';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useReadComposition } from 'features/compositions/services/useReadComposition';
import { useGetCompositionScopesService } from 'features/compositions/services/useGetCompositionScopesService';
import AnimatedLoader from 'components/shared/status/AnimatedLoader';
import { Link } from 'react-router-dom';
import { useCompositionAdvancedOptionsContext } from 'features/compositions/context/CompositionAdvancedOptionsContext';

const EditableCompositionTable = () => {
    const { dark } = useContext(ThemeContext);
    const [editMode, setEditMode] = useState(false);
    const { 
        tempPrivateKey, 
        databaseStructure,
        selectedComposition, 
        setEditedRowsData, 
        selectAllRows, 
        clearSelection, 
        selectedRows, 
        loading
    } = useCompositionsContext();
    const { readComposition } = useReadComposition();
    const { getCompositionScopes } = useGetCompositionScopesService();
    const [newRowsMode, setNewRowsMode] = useState(false);
    const [newRowsData, setNewRowsData] = useState([]);
    const { limit, page, reverse, localReverse } = useCompositionAdvancedOptionsContext();
    const tableBodyRef = useRef(null);

    useEffect(() => {
        if (selectedComposition && selectedComposition.name) {
            getCompositionScopes({ composition: selectedComposition.name });
        }
    }, [selectedComposition?.name]);

    useEffect(() => {
        if (selectedComposition && selectedComposition.name) {
            const offset = (page - 1) * limit;
            readComposition({ composition: selectedComposition.name, offset });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedComposition?.name, selectedComposition?.selected_scope, limit, page]);

    // Reset new rows mode, edited rows data, and edit mode when composition changes
    useEffect(() => {
        setNewRowsMode(false);
        setNewRowsData([]);
        setEditedRowsData({});
        setEditMode(false);
    }, [selectedComposition?.name]);

    const handleSelectAll = () => {
        const primaryKeyName = selectedComposition?.structure?.indexes?.[0]?.name;
        if (!primaryKeyName || !Array.isArray(selectedComposition?.rows)) return;
        const allRowIds = selectedComposition.rows
            .map(r => r?.[primaryKeyName])
            .filter(id => id !== undefined && id !== null);
        if (allRowIds.length === 0) return;
        if (Array.isArray(selectedRows) && selectedRows.length === allRowIds.length) {
            clearSelection();
        } else {
            selectAllRows(allRowIds);
        }
    };

    const scrollToBottom = () => {
        if (tableBodyRef.current) {
            tableBodyRef.current.scrollTop = tableBodyRef.current.scrollHeight;
        }
    };

    if (loading) {
        return (
            <div className={`${styles.empty_space} ${dark ? styles.dark : ''}`}>
                <AnimatedLoader />
                <span>Loading...</span>
            </div>
        );
    }

    if (!loading && Array.isArray(databaseStructure?.compositions) && databaseStructure.compositions.length === 0) {
        return (
            <div className={`${styles.empty_space} ${dark ? styles.dark : ''}`}>
                <span>You have not created any compositions yet.</span>
                <Link to="/compositions/add-composition" className={styles.create_composition_link}>Create composition</Link>
            </div>
        );
    }

    return (
        <div className={`${styles.editable_composition_table} ${dark ? styles.dark : ''}`}>
            <CompositionTableToolbar editMode={editMode} setEditMode={setEditMode} />

            <div className={styles.composition_table}>
                <div className={styles.composition_table_head}>
                    <button className={styles.select_all_btn} onClick={handleSelectAll}>
                        <DoubleCheckIcon />
                    </button>
                    {selectedComposition?.structure?.indexes && Array.isArray(selectedComposition.structure.indexes) &&
                        selectedComposition.structure.indexes.map((i) => (
                            <span key={i.meta_name}>{i.name}</span>
                        ))
                    }
                    {selectedComposition?.structure?.fields && Array.isArray(selectedComposition.structure.fields) &&
                        selectedComposition.structure.fields.map((f) => (
                            <span key={f.meta_name}>{f.name}</span>
                        ))
                    }
                </div>
                <div className={styles.table_body} ref={tableBodyRef}>
                    {selectedComposition?.rows && Array.isArray(selectedComposition.rows) && localReverse && !reverse ?
                        [...selectedComposition.rows].reverse().map((r) => {
                            const primaryKeyName = selectedComposition.structure.indexes[0]?.name;
                            const primaryKeyValue = r[primaryKeyName];
                            return (
                                <EditableCompositionRow
                                    key={primaryKeyValue}
                                    id={primaryKeyValue}
                                    editMode={editMode}
                                />
                            );
                        })
                        : selectedComposition.rows.map((r) => {
                            const primaryKeyName = selectedComposition.structure.indexes[0]?.name;
                            const primaryKeyValue = r[primaryKeyName];
                            return (
                                <EditableCompositionRow
                                    key={primaryKeyValue}
                                    id={primaryKeyValue}
                                    editMode={editMode}
                                />
                            );
                        })
                    }
                    {newRowsMode && <AddNewCompositionRows newRowsData={newRowsData} setNewRowsData={setNewRowsData} />}
                    {!selectedComposition?.rows.length && (
                        <span className={styles.no_rows_message}>
                            No rows found for "{selectedComposition?.name}" in scope "{selectedComposition?.selected_scope}".
                        </span>
                    )}
                </div>
            </div>
            <RequireAuth permission={selectedComposition?.permission} hasTempKey={!!tempPrivateKey}>
                <AddNewRowsControl
                    newRowsMode={newRowsMode}
                    setNewRowsMode={setNewRowsMode}
                    newRowsData={newRowsData}
                    setNewRowsData={setNewRowsData}
                    scrollToBottom={scrollToBottom}
                />
            </RequireAuth>
        </div>
    )
}

export default EditableCompositionTable;